import { getBaseRole } from '@/helpers/customRoles';
import { FC, useEffect, useMemo, useState } from 'react'
import mclasses from '../../styles/modal.module.css'
import classes from './index.module.css'
import cn from 'classnames'
import Modal from '@common/Modal'
import { crmApi } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import { isError } from '@/helpers/checkQuery'
import { useAsync } from 'react-use'
import Select from '../base/Select/Select'
import { LeadState } from '@/api/types/crm/crm.types'
import { useSelector } from 'react-redux'
import { selectProfile } from '@store/profile.reducer'

export type LeadFunnelBulkManagerRow = { id: number; name: string }

interface LeadFunnelsBulkChangeManagerModalProps {
  items: LeadFunnelBulkManagerRow[]
  leadStateType: LeadState
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

const LeadFunnelsBulkChangeManagerModal: FC<
  LeadFunnelsBulkChangeManagerModalProps
> = ({ items, leadStateType, open, onClose, onSuccess }) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const currentRole = profile?.role

  const managersState = useAsync(crmApi.getAllManagers, [])
  const managers = managersState.value?.data?.data || []

  useEffect(() => {
    if (managersState.error) {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось загрузить список менеджеров',
        type: 'error',
      })
    }
  }, [managersState.error, addAlert])

  const [managerId, setManagerId] = useState<number | null>(null)
  const managerOptions = useMemo(
    () =>
      managers.map((manager) => ({
        value: Number(manager.id),
        label: manager.fio,
      })),
    [managers],
  )

  const currentManagerOption = useMemo(() => {
    const profileUserId = Number(profile?.id)
    if (!Number.isFinite(profileUserId)) return null

    return (
      managerOptions.find((option) => {
        const manager = managers.find((item: any) => Number(item.id) === option.value)
        const managerUserId = Number(manager?.user?.id)
        return Number.isFinite(managerUserId) && managerUserId === profileUserId
      }) ?? null
    )
  }, [managerOptions, managers, profile?.id])

  const isSalesManager = getBaseRole(currentRole) === 'sales-manager'

  useEffect(() => {
    if (!open) return

    if (isSalesManager) {
      setManagerId(currentManagerOption?.value ?? null)
      return
    }

    setManagerId(null)
  }, [open, items, isSalesManager, currentManagerOption?.value])

  useEffect(() => {
    if (!open || items.length > 0) return
    addAlert({
      title: 'Ошибка',
      text: 'У выбранных строк нет идентификатора заявки (воронка).',
      type: 'error',
    })
    onClose()
  }, [open, items.length, onClose, addAlert])

  const handleConfirm = () => {
    if (!items.length) {
      onClose()
      return
    }

    if (!managerId) {
      addAlert({
        title: 'Ошибка',
        text: 'Выберите менеджера',
        type: 'error',
      })
      return
    }

    crmApi.leads
      .changeLeadFunnelsState(
        { type: leadStateType, manager_id: managerId },
        {
          lead_funnel_ids: items.map((i) => i.id),
        },
      )
      .then((res) => {
        if (isError(res)) throw new Error()
        addAlert({
          title: 'Успешно',
          text: 'Менеджер успешно назначен',
          type: 'success',
        })
        onSuccess?.()
      })
      .catch(() => {
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось сменить менеджера',
          type: 'error',
        })
        onClose()
      })
  }

  const namesLine = items
    .map((i) => i.name)
    .filter(Boolean)
    .join(', ')

  return (
    <Modal
      active={open}
      className={mclasses.modalWrapper as any}
      setActive={() => onClose()}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Кому передать {items.length === 1 ? 'заявку' : 'заявки'}?
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
        <div className={cn(mclasses.formWrapper, classes.formWrapper)}>
          <div>
            <p className={mclasses.label}>Заявки</p>
            <p>{namesLine || '—'}</p>
          </div>
          <div>
            <p className={mclasses.label}>ФИО менеджера</p>
            <Select
              showSearch
              placeholder="Выберите менеджера"
              value={managerId ?? undefined}
              onChange={(value) => setManagerId(value)}
              allowClear={!isSalesManager}
              options={managerOptions}
              loading={managersState.loading}
              disabled={isSalesManager}
            />
          </div>
        </div>
        <div className={mclasses.actions}>
          <Button color="gray" onClick={() => onClose()}>
            Отменить
          </Button>
          <Button color="primary" onClick={() => handleConfirm()}>
            Подтвердить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default LeadFunnelsBulkChangeManagerModal
